import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Images } from "lucide-react";
import { imgSrc, BRL, waLink } from "@/lib/api";

const ProductModal = ({ product, onClose }) => {
  const [photo, setPhoto] = useState(0);
  if (!product) return null;
  const images = product.images?.length ? product.images : [""];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="product-modal"
    >
      <div className="absolute inset-0 bg-espresso/60 backdrop-blur-sm" onClick={onClose} data-testid="product-modal-backdrop" />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 60, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-cream w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl grid md:grid-cols-2"
      >
        <button
          onClick={onClose}
          data-testid="product-modal-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-espresso hover:bg-sand transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="bg-sand p-4 sm:p-6">
          <div className="rounded-2xl overflow-hidden bg-white border border-wood/10">
            <img
              src={imgSrc(images[photo])}
              alt={product.name}
              className="w-full h-72 sm:h-96 object-cover"
              data-testid="product-modal-image"
            />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setPhoto(i)}
                  data-testid={`product-modal-thumb-${i}`}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                    photo === i ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={imgSrc(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 sm:p-8 flex flex-col">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand">{product.category}</p>
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-espresso mt-2 leading-tight" data-testid="product-modal-name">
            {product.name}
          </h3>
          <p className="font-display text-3xl text-brand font-bold mt-4" data-testid="product-modal-price">
            {BRL(product.price)}
          </p>
          {product.description && (
            <p className="text-sm sm:text-base text-wood/70 leading-relaxed mt-4 whitespace-pre-line">
              {product.description}
            </p>
          )}
          <div className="mt-auto pt-6">
            <a
              href={waLink(product.name, product.price)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-modal-whatsapp"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold px-6 py-4 rounded-full transition-colors shadow-lg shadow-[#25D366]/25"
            >
              <MessageCircle className="w-5 h-5" />
              Pedir pelo WhatsApp
            </a>
            <p className="text-xs text-wood/50 text-center mt-3">
              Você será atendido(a) direto no WhatsApp (51) 98997-5814
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Catalog = ({ products, categories: propCategories }) => {
  const derived = useMemo(
    () => [...new Set(products.map((p) => p.category))],
    [products]
  );
  const categories = propCategories?.length
    ? [...new Set([...propCategories, ...derived])]
    : derived;
  const [activeCat, setActiveCat] = useState("Todos");
  const [selected, setSelected] = useState(null);

  const visible = activeCat === "Todos" ? products : products.filter((p) => p.category === activeCat);

  return (
    <section id="catalogo" className="relative bg-cream py-16 md:py-24" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand mb-3">Nosso catálogo</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-espresso">
            Escolha o presente perfeito
          </h2>
          <p className="font-script text-2xl text-amberbrand mt-2">toque em um produto para ver as fotos de pertinho</p>
        </motion.div>

        <div className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-cream/90 backdrop-blur-md">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-filter">
            {["Todos", ...categories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCat(cat)}
                data-testid={`category-pill-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  activeCat === cat
                    ? "bg-espresso text-cream border-espresso"
                    : "bg-white text-wood/70 border-wood/15 hover:border-brand hover:text-brand"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 mt-8">
          <AnimatePresence mode="popLayout">
            {visible.map((prod, i) => (
              <motion.button
                layout
                key={prod.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: Math.min(i % 8, 4) * 0.06 }}
                onClick={() => setSelected(prod)}
                data-testid={`product-card-${prod.id}`}
                className="group text-left bg-white rounded-2xl border border-wood/10 shadow-[0_4px_20px_rgba(74,53,37,0.05)] hover:shadow-[0_16px_40px_rgba(74,53,37,0.14)] hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-sand">
                  {prod.images?.[0] ? (
                    <img
                      src={imgSrc(prod.images[0])}
                      alt={prod.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-wood/30">
                      <Images className="w-10 h-10" />
                    </div>
                  )}
                  {prod.images?.length > 1 && (
                    <span className="absolute top-3 right-3 bg-espresso/70 text-cream text-xs px-2.5 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
                      <Images className="w-3.5 h-3.5" />
                      {prod.images.length} fotos
                    </span>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-brand/80">{prod.category}</p>
                  <h3 className="font-display text-lg font-semibold text-espresso leading-snug mt-1 min-h-[3rem]">
                    {prod.name}
                  </h3>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-display text-xl font-bold text-espresso" data-testid={`product-price-${prod.id}`}>
                      {BRL(prod.price)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#128C7E] bg-[#25D366]/10 px-3 py-1.5 rounded-full group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                      <MessageCircle className="w-3.5 h-3.5" />
                      Pedir
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};
